/** Consolidation, 8/20 EMA, and relative-strength measurements (api/scan.js + App.jsx). */

function smaSlice(arr, n) {
  if (!arr?.length || arr.length < n) return null;
  return arr.slice(0, n).reduce((s, v) => s + v, 0) / n;
}

function calcAtrN(highs, lows, closes, n) {
  if (!closes?.length || closes.length < n + 1) return null;
  const trs = [];
  for (let i = 0; i < n && i < closes.length - 1; i++) {
    trs.push(Math.max(
      highs[i] - lows[i],
      Math.abs(highs[i] - closes[i + 1]),
      Math.abs(lows[i] - closes[i + 1]),
    ));
  }
  return trs.length >= n ? trs.reduce((s, v) => s + v, 0) / n : null;
}

function rangePctWindow(highs, lows, closes, window) {
  const h = highs.slice(0, window);
  const l = lows.slice(0, window);
  const c = closes.slice(0, window);
  if (c.length < window) return null;
  const periodHigh = Math.max(...h);
  const periodLow = Math.min(...l);
  const avgClose = c.reduce((s, v) => s + v, 0) / c.length;
  if (!avgClose) return null;
  return {
    rangePercent: ((periodHigh - periodLow) / avgClose) * 100,
    rangeHigh: periodHigh,
    rangeLow: periodLow,
  };
}

function countConsolidationDays(highs, lows, closes, window, maxPct) {
  let days = 0;
  for (let offset = 0; offset + window <= closes.length; offset++) {
    const slice = rangePctWindow(
      highs.slice(offset),
      lows.slice(offset),
      closes.slice(offset),
      window,
    );
    if (!slice || slice.rangePercent > maxPct) break;
    days++;
  }
  return days;
}

export function buildConsolidation(highs, lows, closes, volumes) {
  const none = {
    active: false,
    type: "NONE",
    days: 0,
    rangePercent: null,
    volumeDeclining: false,
    atrContracting: false,
    priorUptrend: false,
    rangeHigh: null,
    rangeLow: null,
    label: null,
  };
  if (!highs?.length || !lows?.length || !closes?.length || closes.length < 20) return none;

  const tiers = [
    { type: "TIGHT_COIL", window: 10, maxPct: 3, minDays: 10 },
    { type: "CONSOLIDATION", window: 15, maxPct: 5, minDays: 15 },
    { type: "BASE_BUILDING", window: 20, maxPct: 8, minDays: 20 },
  ];

  for (const tier of tiers) {
    const rw = rangePctWindow(highs, lows, closes, tier.window);
    if (!rw || rw.rangePercent > tier.maxPct) continue;
    const days = countConsolidationDays(highs, lows, closes, tier.window, tier.maxPct);
    if (days < tier.minDays) continue;

    const recentVol = volumes?.slice(0, 5) || [];
    const priorVol = volumes?.slice(5, 10) || [];
    const recentAvg = recentVol.length ? recentVol.reduce((s, v) => s + (v || 0), 0) / recentVol.length : null;
    const priorAvg = priorVol.length ? priorVol.reduce((s, v) => s + (v || 0), 0) / priorVol.length : null;
    const volumeDeclining = recentAvg != null && priorAvg != null && recentAvg < priorAvg;

    const atr5 = calcAtrN(highs, lows, closes, 5);
    const atr14 = calcAtrN(highs, lows, closes, 14);
    const atrContracting = atr5 != null && atr14 != null && atr5 < atr14 * 0.8;

    const price30Ago = closes[30] ?? null;
    const priorUptrend = price30Ago != null && rw.rangeHigh != null
      && price30Ago <= rw.rangeHigh * 0.95;

    let label = null;
    if (tier.type === "TIGHT_COIL") {
      label = `🔄 TIGHT COIL — ${days} days, ${rw.rangePercent.toFixed(1)}% range`;
    } else if (tier.type === "CONSOLIDATION") {
      label = `📦 CONSOLIDATION — ${days} days`;
    } else {
      label = `🏗 BASE BUILDING — ${days} days`;
    }

    return {
      active: true,
      type: tier.type,
      days,
      rangePercent: Math.round(rw.rangePercent * 100) / 100,
      volumeDeclining,
      atrContracting,
      priorUptrend,
      rangeHigh: rw.rangeHigh,
      rangeLow: rw.rangeLow,
      label,
    };
  }

  return none;
}

function emaSeriesLatestFirst(closes, period) {
  if (!closes?.length || closes.length < period) return [];
  const chrono = [...closes].reverse();
  const k = 2 / (period + 1);
  let e = chrono.slice(0, period).reduce((s, v) => s + v, 0) / period;
  const seriesChrono = [e];
  for (let i = period; i < chrono.length; i++) {
    e = chrono[i] * k + e * (1 - k);
    seriesChrono.push(e);
  }
  const pad = new Array(period - 1).fill(null);
  return [...seriesChrono, ...pad].reverse().slice(0, closes.length);
}

function emaSlopeLabel(current, prior, risePct, fallPct) {
  if (current == null || prior == null || !prior) return "FLAT";
  const chg = ((current - prior) / prior) * 100;
  if (chg > risePct) return "RISING";
  if (chg < fallPct) return "FALLING";
  return "FLAT";
}

export function buildEmaMetrics(closes, price) {
  const empty = {
    ema8: null,
    ema20: null,
    emaAlignment: null,
  };
  if (!closes?.length || closes.length < 21 || price == null) return empty;

  const ema8Arr = emaSeriesLatestFirst(closes, 8);
  const ema20Arr = emaSeriesLatestFirst(closes, 20);
  const ema8Val = ema8Arr[0];
  const ema20Val = ema20Arr[0];
  if (ema8Val == null || ema20Val == null) return empty;

  const ema8Slope = emaSlopeLabel(ema8Arr[0], ema8Arr[3], 0.3, -0.3);
  const ema20Slope = emaSlopeLabel(ema20Arr[0], ema20Arr[5], 0.2, -0.2);
  const priceAbove8 = price >= ema8Val;
  const priceAbove20 = price >= ema20Val;

  const crossoverBullish = ema8Arr[3] != null && ema20Arr[3] != null
    && ema8Arr[3] < ema20Arr[3] && ema8Val > ema20Val;
  const crossoverBearish = ema8Arr[3] != null && ema20Arr[3] != null
    && ema8Arr[3] > ema20Arr[3] && ema8Val < ema20Val;

  const crossoverBullishFresh = (ema8Arr[1] != null && ema20Arr[1] != null && ema8Arr[1] <= ema20Arr[1] && ema8Val > ema20Val)
    || (ema8Arr[2] != null && ema20Arr[2] != null && ema8Arr[2] <= ema20Arr[2] && ema8Val > ema20Val);
  const crossoverBearishFresh = (ema8Arr[1] != null && ema20Arr[1] != null && ema8Arr[1] >= ema20Arr[1] && ema8Val < ema20Val)
    || (ema8Arr[2] != null && ema20Arr[2] != null && ema8Arr[2] >= ema20Arr[2] && ema8Val < ema20Val);

  const priceAt8EMA = Math.abs(price - ema8Val) / ema8Val <= 0.005;
  const priceAt20EMA = Math.abs(price - ema20Val) / ema20Val <= 0.01;

  const strongBullish = ema8Val > ema20Val
    && ema8Slope === "RISING" && ema20Slope === "RISING"
    && priceAbove8 && priceAbove20;
  const strongBearish = ema8Val < ema20Val
    && ema8Slope === "FALLING" && ema20Slope === "FALLING"
    && !priceAbove8 && !priceAbove20;

  let alignment = "NEUTRAL";
  if (strongBullish) alignment = "STRONG_BULL";
  else if (strongBearish) alignment = "STRONG_BEAR";
  else if (ema8Val > ema20Val && (ema8Slope === "RISING" || ema20Slope === "RISING")) alignment = "BULL";
  else if (ema8Val < ema20Val && (ema8Slope === "FALLING" || ema20Slope === "FALLING")) alignment = "BEAR";

  return {
    ema8: {
      value: Math.round(ema8Val * 100) / 100,
      slope: ema8Slope,
      priceAbove: priceAbove8,
    },
    ema20: {
      value: Math.round(ema20Val * 100) / 100,
      slope: ema20Slope,
      priceAbove: priceAbove20,
    },
    emaAlignment: {
      strongBullish,
      strongBearish,
      crossoverBullish,
      crossoverBearish,
      crossoverBullishFresh,
      crossoverBearishFresh,
      priceAt8EMA,
      priceAt20EMA,
      alignment,
    },
  };
}

function relReturn(closes, n) {
  if (!closes?.length || closes.length <= n || !closes[n]) return null;
  return ((closes[0] - closes[n]) / closes[n]) * 100;
}

function rsVs(spyCloses, tickerCloses, n) {
  const tr = relReturn(tickerCloses, n);
  const sr = relReturn(spyCloses, n);
  if (tr == null || sr == null) return null;
  return tr - sr;
}

export function mapRsRating(vsSpy63d) {
  if (vsSpy63d == null) return null;
  if (vsSpy63d > 20) return Math.min(99, Math.round(95 + (vsSpy63d - 20) * 0.2));
  if (vsSpy63d > 15) return 90 + Math.round((vsSpy63d - 15) * 1);
  if (vsSpy63d > 10) return 80 + Math.round((vsSpy63d - 10) * 2);
  if (vsSpy63d > 5) return 70 + Math.round((vsSpy63d - 5) * 2);
  if (vsSpy63d > 2) return 60 + Math.round((vsSpy63d - 2) * 3.33);
  if (vsSpy63d >= 0) return 50 + Math.round(vsSpy63d * 5);
  if (vsSpy63d >= -10) return Math.max(20, 50 + Math.round(vsSpy63d * 3));
  return Math.max(1, 20 + Math.round(vsSpy63d * 2));
}

export function buildRelativeStrength(closes, spyCloses, sectorCloses, sectorEtf = null) {
  const empty = {
    vs_spy_3d: null,
    vs_spy_10d: null,
    vs_spy_21d: null,
    vs_spy_63d: null,
    vs_sector_5d: null,
    vs_sector_21d: null,
    rs_rating: null,
    rs_line_new_high: false,
    rs_line_trend: null,
    sector_leader: false,
    sector_laggard: false,
    sector_etf: sectorEtf || null,
  };
  if (!closes?.length || !spyCloses?.length) return empty;

  const len = Math.min(closes.length, spyCloses.length);
  const c = closes.slice(0, len);
  const spy = spyCloses.slice(0, len);

  const vs_spy_3d = rsVs(spy, c, 3);
  const vs_spy_10d = rsVs(spy, c, 10);
  const vs_spy_21d = rsVs(spy, c, 21);
  const vs_spy_63d = rsVs(spy, c, 63);
  const rs_rating = mapRsRating(vs_spy_63d);

  let rs_line_new_high = false;
  let rs_line_trend = null;
  const rsLineNow = spy[0] ? c[0] / spy[0] : null;
  if (rsLineNow != null) {
    const lookback = Math.min(252, len);
    let rsHigh = -Infinity;
    for (let i = 0; i < lookback; i++) {
      if (spy[i] > 0) rsHigh = Math.max(rsHigh, c[i] / spy[i]);
    }
    if (Number.isFinite(rsHigh)) {
      rs_line_new_high = rsLineNow >= rsHigh * 0.98;
    }
    const rsLine10 = spy[10] ? c[10] / spy[10] : null;
    if (rsLine10 != null) {
      const chg = ((rsLineNow - rsLine10) / rsLine10) * 100;
      rs_line_trend = chg > 0.5 ? "RISING" : chg < -0.5 ? "FALLING" : "FLAT";
    }
  }

  let vs_sector_5d = null;
  let vs_sector_21d = null;
  if (sectorCloses?.length >= 22) {
    const slen = Math.min(c.length, sectorCloses.length);
    const sc = sectorCloses.slice(0, slen);
    const tc = c.slice(0, slen);
    vs_sector_5d = rsVs(sc, tc, 5);
    vs_sector_21d = rsVs(sc, tc, 21);
  }

  return {
    vs_spy_3d,
    vs_spy_10d,
    vs_spy_21d,
    vs_spy_63d,
    vs_sector_5d,
    vs_sector_21d,
    rs_rating,
    rs_line_new_high,
    rs_line_trend,
    sector_leader: vs_sector_5d != null && vs_sector_5d > 1.5,
    sector_laggard: vs_sector_5d != null && vs_sector_5d < -1.5,
    sector_etf: sectorEtf || null,
  };
}

export function attachScanMeasurements(payload, { spyCloses, sectorCloses, sectorEtf } = {}) {
  if (!payload?.closes?.length) return payload;
  const { closes, highs, lows, volumes } = payload;
  const price = closes[0];

  try {
    payload.consolidation = buildConsolidation(highs, lows, closes, volumes);
  } catch {
    payload.consolidation = { active: false, type: "NONE", days: 0, rangePercent: null, volumeDeclining: false, atrContracting: false, priorUptrend: false, rangeHigh: null, rangeLow: null, label: null };
  }

  try {
    const ema = buildEmaMetrics(closes, price);
    payload.ema8 = ema.ema8;
    payload.ema20 = ema.ema20;
    payload.emaAlignment = ema.emaAlignment;
  } catch {
    payload.ema8 = null;
    payload.ema20 = null;
    payload.emaAlignment = null;
  }

  try {
    payload.relativeStrength = buildRelativeStrength(closes, spyCloses, sectorCloses, sectorEtf);
  } catch {
    payload.relativeStrength = null;
  }

  return payload;
}

export function computeMeasurementScoreBonus(consolidation, ema8, ema20, emaAlignment, relativeStrength) {
  let bonus = 0;
  const breakdown = { consolidation: 0, ema: 0, rs: 0 };

  const c = consolidation;
  if (c?.active && c.priorUptrend) {
    if (c.type === "TIGHT_COIL") { breakdown.consolidation += 3; }
    else if (c.type === "CONSOLIDATION") { breakdown.consolidation += 2; }
    else if (c.type === "BASE_BUILDING") { breakdown.consolidation += 1; }
  }
  if (c?.active && c.volumeDeclining) breakdown.consolidation += 1;
  if (c?.active && c.atrContracting) breakdown.consolidation += 0.5;

  const a = emaAlignment;
  if (a?.strongBullish) breakdown.ema += 2;
  if (a?.crossoverBullishFresh) breakdown.ema += 2;
  if (ema8?.priceAbove && ema20?.priceAbove && ema8.slope === "RISING" && ema20.slope === "RISING") breakdown.ema += 1;
  if (a?.priceAt8EMA && ema8?.slope !== "FALLING" && ema20?.slope !== "FALLING") breakdown.ema += 1;
  if (a?.priceAt20EMA && ema8?.priceAbove && ema20?.priceAbove) breakdown.ema += 1.5;
  if (a?.strongBearish) breakdown.ema -= 2;
  if (a?.crossoverBearishFresh) breakdown.ema -= 2;
  if (ema8?.priceAbove === false && ema20?.priceAbove === false && ema8?.slope === "FALLING" && ema20?.slope === "FALLING") breakdown.ema -= 1;

  const rs = relativeStrength;
  if (rs?.rs_line_new_high) breakdown.rs += 2;
  if (rs?.rs_rating != null && rs.rs_rating >= 80 && rs.rs_line_trend === "RISING") breakdown.rs += 2;
  else if (rs?.rs_rating != null && rs.rs_rating >= 60) breakdown.rs += 1;
  if (rs?.rs_rating != null && rs.rs_rating < 20) breakdown.rs -= 2;
  else if (rs?.rs_rating != null && rs.rs_rating < 40) breakdown.rs -= 1;
  if (rs?.sector_leader) breakdown.rs += 1;
  if (rs?.sector_laggard) breakdown.rs -= 1;

  bonus = breakdown.consolidation + breakdown.ema + breakdown.rs;
  return { bonus, breakdown };
}

export function emaAlignmentDisplay(emaAlignment) {
  if (!emaAlignment) return { text: "~8/20", color: "gray" };
  if (emaAlignment.crossoverBullishFresh || emaAlignment.crossoverBearishFresh) {
    return { text: "✗8/20", color: "yellow" };
  }
  if (emaAlignment.strongBullish || emaAlignment.alignment === "BULL" || emaAlignment.alignment === "STRONG_BULL") {
    return { text: "↑8/20", color: "green" };
  }
  if (emaAlignment.strongBearish || emaAlignment.alignment === "BEAR" || emaAlignment.alignment === "STRONG_BEAR") {
    return { text: "↓8/20", color: "red" };
  }
  return { text: "~8/20", color: "gray" };
}

export function consolidationActionLabel(consolidation) {
  if (!consolidation?.active) return null;
  if (consolidation.type === "TIGHT_COIL" && consolidation.priorUptrend) {
    return `🔄 TIGHT COIL · ${consolidation.days}d`;
  }
  if (consolidation.type === "CONSOLIDATION") {
    return `📦 COILING · ${consolidation.days}d`;
  }
  if (consolidation.type === "BASE_BUILDING") {
    return `🏗 BASING · ${consolidation.days}d`;
  }
  return null;
}

export function consolidationSignalBadge(consolidation) {
  if (!consolidation?.active) return null;
  if (consolidation.type === "TIGHT_COIL") return `🔄 COIL ${consolidation.days}d`;
  if (consolidation.type === "CONSOLIDATION") return `📦 COIL ${consolidation.days}d`;
  if (consolidation.type === "BASE_BUILDING") return `📦 BASE ${consolidation.days}d`;
  return null;
}
